'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { getDisplayName } from '@/lib/displayName';

interface CommentUser {
  id: string;
  firstName: string;
  avatarUrl?: string | null;
}

interface CommentReply {
  id: string;
  content: string;
  createdAt: string;
  user: CommentUser;
}

interface Comment {
  id: string;
  content: string;
  createdAt: string;
  user: CommentUser;
  replies: CommentReply[];
}

interface Props {
  slug: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU');
}

function Avatar({ user }: { user: CommentUser }) {
  const name = getDisplayName(user);
  const initial = name.charAt(0).toUpperCase();
  if (user.avatarUrl) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={user.avatarUrl} alt={name} className="h-9 w-9 flex-shrink-0 rounded-full object-cover" />;
  }
  return (
    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-muted text-sm font-semibold text-brand">
      {initial}
    </div>
  );
}

function extractErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: { message?: string } } } };
  return e?.response?.data?.error?.message || fallback;
}

export default function BlogComments({ slug }: Props) {
  const user = useAuthStore(state => state.user);
  const queryClient = useQueryClient();
  const [content, setContent] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [replyError, setReplyError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['blog-comments', slug],
    queryFn: async () => {
      const res = await api.get(`/blog/articles/${slug}/comments`);
      return res.data.data as Comment[];
    },
    staleTime: 0,
  });

  const createMutation = useMutation({
    mutationFn: async (payload: { content: string; parentId?: string }) => {
      const res = await api.post(`/blog/articles/${slug}/comments`, payload);
      return res.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['blog-comments', slug] });
      if (variables.parentId) {
        setReplyContent('');
        setReplyTo(null);
        setReplyError(null);
      } else {
        setContent('');
        setFormError(null);
      }
    },
    onError: (err, variables) => {
      const message = extractErrorMessage(err, 'Не удалось отправить комментарий. Попробуйте позже.');
      if (variables.parentId) {
        setReplyError(message);
      } else {
        setFormError(message);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/blog/comments/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-comments', slug] });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = content.trim();
    if (!trimmed) return;
    createMutation.mutate({ content: trimmed });
  };

  const handleReplySubmit = (e: React.FormEvent, parentId: string) => {
    e.preventDefault();
    const trimmed = replyContent.trim();
    if (!trimmed) return;
    createMutation.mutate({ content: trimmed, parentId });
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Удалить комментарий?')) {
      deleteMutation.mutate(id);
    }
  };

  const openReplyForm = (id: string) => {
    setReplyError(null);
    setReplyContent('');
    setReplyTo(prev => (prev === id ? null : id));
  };

  const comments = data ?? [];

  return (
    <section className="mt-12">
      <h2 className="mb-4 text-xl font-bold text-text-primary">Комментарии</h2>

      {isLoading && (
        <div className="space-y-3">
          {[0, 1].map(i => (
            <div key={i} className="h-16 animate-pulse rounded-xl bg-bg-subtle" />
          ))}
        </div>
      )}

      {isError && !isLoading && (
        <p className="text-sm text-error">Не удалось загрузить комментарии. Попробуйте обновить страницу.</p>
      )}

      {!isLoading && !isError && comments.length === 0 && (
        <p className="text-sm text-text-secondary">Будьте первым, кто оставит комментарий!</p>
      )}

      {!isLoading && !isError && comments.length > 0 && (
        <ul className="space-y-5">
          {comments.map(comment => (
            <li key={comment.id} className="space-y-3">
              <div className="flex gap-3">
                <Avatar user={comment.user} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-semibold text-text-primary">{getDisplayName(comment.user)}</span>
                    <span className="text-xs text-text-secondary">{formatDate(comment.createdAt)}</span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm text-text-primary">{comment.content}</p>
                  <div className="mt-2 flex gap-3 text-xs">
                    {user && (
                      <button
                        type="button"
                        onClick={() => openReplyForm(comment.id)}
                        className="font-medium text-brand hover:text-brand-dark"
                      >
                        Ответить
                      </button>
                    )}
                    {user?.id === comment.user.id && (
                      <button
                        type="button"
                        onClick={() => handleDelete(comment.id)}
                        className="font-medium text-error hover:opacity-80"
                      >
                        Удалить
                      </button>
                    )}
                  </div>

                  {replyTo === comment.id && (
                    <form onSubmit={e => handleReplySubmit(e, comment.id)} className="mt-3 space-y-2">
                      <textarea
                        value={replyContent}
                        onChange={e => setReplyContent(e.target.value)}
                        placeholder="Ваш ответ..."
                        rows={2}
                        className="w-full rounded-xl border border-border bg-bg-base p-3 text-sm text-text-primary focus:border-brand focus:outline-none"
                      />
                      {replyError && <p className="text-xs text-error">{replyError}</p>}
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          disabled={createMutation.isPending || !replyContent.trim()}
                          className="rounded-lg bg-brand px-4 py-1.5 text-xs font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
                        >
                          Отправить
                        </button>
                        <button
                          type="button"
                          onClick={() => setReplyTo(null)}
                          className="rounded-lg px-4 py-1.5 text-xs font-semibold text-text-secondary hover:bg-bg-subtle"
                        >
                          Отмена
                        </button>
                      </div>
                    </form>
                  )}

                  {comment.replies?.length > 0 && (
                    <ul className="mt-4 space-y-4 border-l-2 border-border pl-4">
                      {comment.replies.map(reply => (
                        <li key={reply.id} className="flex gap-3">
                          <Avatar user={reply.user} />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-baseline gap-2">
                              <span className="font-semibold text-text-primary">{getDisplayName(reply.user)}</span>
                              <span className="text-xs text-text-secondary">{formatDate(reply.createdAt)}</span>
                            </div>
                            <p className="mt-1 whitespace-pre-wrap break-words text-sm text-text-primary">{reply.content}</p>
                            {user?.id === reply.user.id && (
                              <button
                                type="button"
                                onClick={() => handleDelete(reply.id)}
                                className="mt-2 text-xs font-medium text-error hover:opacity-80"
                              >
                                Удалить
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-6">
        {user ? (
          <form onSubmit={handleSubmit} className="space-y-2">
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Оставьте комментарий..."
              rows={3}
              className="w-full rounded-xl border border-border bg-bg-base p-3 text-sm text-text-primary focus:border-brand focus:outline-none"
            />
            {formError && <p className="text-xs text-error">{formError}</p>}
            <button
              type="submit"
              disabled={createMutation.isPending || !content.trim()}
              className="rounded-lg bg-brand px-5 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
            >
              Отправить
            </button>
          </form>
        ) : (
          <p className="text-sm text-text-secondary">
            <Link href={`/login?next=/blog/${slug}`} className="font-semibold text-brand hover:text-brand-dark">
              Войдите
            </Link>
            , чтобы комментировать
          </p>
        )}
      </div>
    </section>
  );
}
